;(function() {
  'use strict'

  Service.$inject = ["$http", "configService", "userService", "glAuthService", "glUtils", "glDialog", "StateFactory"];
  angular.module('app.core').factory('aiExportService', Service)

  /* @ngInject */
  function Service(
    $http,
    configService,
    userService,
    glAuthService,
    glUtils,
    glDialog,
    StateFactory
  ) {
    var MAX_NOT_FOUND_RETRIES = 20
    var POLL_INTERVAL = 500
    var RETRY_INTERVAL = 1000

    // Shared instances keyed by conversationId. Multiple components calling
    // .get() with the same conversationId receive the same instance, avoiding
    // duplicate polling and keeping state in sync.
    var instances = {}

    return {
      get: get,
    }

    // Returns an export instance for the given conversationId, or null if
    // no conversationId is provided (meaning no AI conversation exists).
    // Passing a $scope binds the instance lifecycle to that scope — when all
    // bound scopes are destroyed, the instance auto-cleans up.
    function get(conversationId, scope) {
      if (!conversationId) return null

      if (!instances[conversationId]) {
        instances[conversationId] = createInstance(conversationId)
      }

      var instance = instances[conversationId]
      instance._refCount++

      // Auto-cleanup: when the component's scope is destroyed, decrement the
      // ref count. When no components reference this instance, destroy it.
      // This means components never need to manually call destroy().
      if (scope) {
        scope.$on('$destroy', function() {
          instance._refCount--
          if (instance._refCount <= 0) {
            instance._destroy()
            delete instances[conversationId]
          }
        })
      }

      return instance
    }

    // Builds auth headers fresh on every request. Both the Bearer token and
    // subscriberId can change if the user logs out and back in or switches
    // subscriber context, so we never cache these values.
    function getAuthHeaders() {
      var headers = {}
      if (glAuthService.hasCredentials()) {
        headers.Authorization = 'Bearer ' + glAuthService.getToken()
      }
      var subscriberId = userService.getSubscriberId()
      if (subscriberId) {
        headers['x-subscriber-id'] = subscriberId
      }
      return headers
    }

    function getFilenameFromResponse(resp) {
      var disposition = resp.headers('Content-Disposition')
      if (!disposition) return null
      var match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]*)/)
      return match && match[1] ? match[1] : null
    }

    // File downloads require XHR ($http) instead of browser navigation because
    // the server requires Authorization and x-subscriber-id headers. Browser
    // navigation (window.open, <a> click) cannot attach custom headers.
    //
    // Because the request goes through XHR, the server's Content-Disposition
    // header does NOT trigger an automatic browser download — the response
    // lands in JS memory as a Blob. We then manually trigger the download
    // using glUtils.downloadFile (creates an <a> element, sets its href to
    // a Blob URL, and clicks it).
    //
    // responseType: 'blob' tells the browser to parse the response as binary
    // data (a Blob object) instead of trying to decode it as JSON or text.
    // This is a client-side instruction — it is not sent to the server.
    function downloadFile(url, defaultFilename) {
      return $http({
        method: 'GET',
        url: url,
        responseType: 'blob',
        headers: getAuthHeaders(),
      })
        .then(function(resp) {
          var filename = getFilenameFromResponse(resp) || defaultFilename
          glUtils.downloadFile(resp.data, null, filename)
        })
        .catch(function() {
          glDialog.alert(
            'Download Error',
            'There was an issue downloading the file. Please try again.'
          )
        })
    }

    function createInstance(conversationId) {
      var pollTimer = null
      var notFoundRetries = 0
      var destroyed = false

      // brief.state transitions:
      //   (unset) → running → ready    (happy path)
      //   (unset) → running → failed   (generation error)
      //   (unset) → failed             (network error)
      // Before any state is set, all .is*() methods return false.
      var instance = {
        _refCount: 0,
        _destroy: destroy,

        conversation: {
          downloading: false,
          download: downloadConversation,
        },

        brief: {
          available: false,
          state: new StateFactory(['running', 'ready', 'failed']),
          error: null,
          downloading: false,
          poll: startPolling,
          download: downloadBrief,
        },
      }

      return instance

      function destroy() {
        destroyed = true
        clearTimeout(pollTimer)
        pollTimer = null
      }

      // Idempotent — safe to call multiple times. Only the first call
      // starts polling; subsequent calls are no-ops if already polling
      // or if a terminal state (ready/failed) has been reached.
      function startPolling() {
        if (destroyed) return
        if (pollTimer) return
        if (instance.brief.state.isReady() || instance.brief.state.isFailed()) {
          return
        }
        pollBriefStatus()
      }

      function pollBriefStatus() {
        $http({
          method: 'GET',
          url: configService.getSurveyAiEditorOrigin(
            '/conversation/' + conversationId + '/brief'
          ),
          headers: _.assign({ Accept: 'application/json' }, getAuthHeaders()),
        })
          .then(function(resp) {
            if (destroyed) return
            notFoundRetries = 0
            var status = resp.data.status
            if (status === 'running') {
              instance.brief.available = true
              instance.brief.state.running()
              schedulePoll(POLL_INTERVAL)
            } else if (status === 'ready') {
              instance.brief.available = true
              instance.brief.state.ready()
            } else if (status === 'failed') {
              instance.brief.available = true
              instance.brief.state.failed()
              instance.brief.error =
                resp.data.error || 'Brief generation failed'
            }
          })
          .catch(function(err) {
            if (destroyed) return
            if (err.status === 404) {
              notFoundRetries++
              if (notFoundRetries < MAX_NOT_FOUND_RETRIES) {
                schedulePoll(RETRY_INTERVAL)
              }
            } else {
              instance.brief.available = true
              instance.brief.state.failed()
              instance.brief.error = 'Failed to check brief status'
            }
          })
      }

      function schedulePoll(delay) {
        pollTimer = setTimeout(function() {
          pollTimer = null
          if (!destroyed) pollBriefStatus()
        }, delay)
      }

      function downloadBrief() {
        if (instance.brief.downloading) return
        instance.brief.downloading = true
        var url = configService.getSurveyAiEditorOrigin(
          '/conversation/' + conversationId + '/brief?format=docx'
        )
        downloadFile(url, 'ai-brief.docx').finally(function() {
          instance.brief.downloading = false
        })
      }

      function downloadConversation() {
        if (instance.conversation.downloading) return
        instance.conversation.downloading = true
        var url = configService.getSurveyAiEditorOrigin(
          '/conversations/' + conversationId + '/download'
        )
        downloadFile(url, 'ai-conversation.docx').finally(function() {
          instance.conversation.downloading = false
        })
      }
    }
  }
})()
